import React, { useState, useMemo, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Map,
  Route as RouteIcon,
  Home,
  AlertCircle,
  Users,
  Building2,
  CheckCircle,
  PanelLeftClose,
  PanelLeftOpen,
  X,
  Eye,
  Sparkles,
  Shield,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  MessageCircle,
  BarChart3
  // UserCog
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import logo from '@/assets/logos/logo_transparent.png';
import { VehixName } from '@/components/brand/VehixWordmark';
import { ProfileDialog } from '@/components/ProfileDialog';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { useWhatsNew } from '@/hooks/useWhatsNew';
import { WhatsNewPopover } from '@/components/WhatsNewPopover';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { profileAPI } from '@/lib/supabase/profile';
import { useTenants } from '@/hooks/useTenants';
import { useTenant, useCurrentTenantId } from '@/contexts/TenantContext';
import { fetchDealersData } from '@/hooks/useDealers';
import { fetchCampaignsData } from '@/hooks/useCampaigns';
import { fetchTechniciansData } from '@/hooks/useTechniciansQuery';

// Define navigation item interface
interface NavItem {
  title: string;
  url: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: React.ComponentType<any>;
  isActive?: boolean;
  requiredPermission?: {
    resource: string;
    action: string;
  };
}

// Define navigation group interface
interface NavGroup {
  title: string;
  items: NavItem[];
}

interface StandaloneSidebarProps {
  user?: {
    email?: string;
    name?: string;
    avatar?: string;
    role?: string;
    approved?: boolean;
  };
  isMobileMenuOpen?: boolean;
  onMobileMenuToggle?: (open: boolean) => void;
}

// Workflow Step Component
const WorkflowStep: React.FC<{
  step: number;
  currentStep: number;
  label: string;
  isOpen: boolean;
}> = ({ step, currentStep, label, isOpen }) => (
  <div className='flex items-center gap-2'>
    <div
      className={cn(
        'flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold',
        'transition-all duration-200',
        currentStep >= step
          ? 'bg-primary text-white shadow-lg shadow-primary/50'
          : 'bg-sidebar-accent text-sidebar-foreground/40 border border-sidebar-foreground/20'
      )}
    >
      {currentStep > step ? <CheckCircle className='h-3 w-3' /> : step}
    </div>
    {isOpen && (
      <span
        className={cn(
          'text-xs font-medium',
          currentStep >= step ? 'text-sidebar-foreground' : 'text-sidebar-foreground/40'
        )}
      >
        {label}
      </span>
    )}
  </div>
);

// Workflow Progress Component
const WorkflowProgress: React.FC<{ currentStep?: number; isOpen: boolean }> = ({
  currentStep,
  isOpen
}) => {
  if (currentStep === undefined || !isOpen) return null;

  return (
    <div className='mt-4 px-2'>
      <div className='flex items-center justify-between'>
        <WorkflowStep step={1} currentStep={currentStep} label='Search' isOpen={isOpen} />
        <div
          className={cn(
            'flex-1 h-0.5 mx-1 transition-colors',
            currentStep >= 2 ? 'bg-primary' : 'bg-sidebar-foreground/20'
          )}
        />
        <WorkflowStep step={2} currentStep={currentStep} label='Plan' isOpen={isOpen} />
        <div
          className={cn(
            'flex-1 h-0.5 mx-1 transition-colors',
            currentStep >= 3 ? 'bg-primary' : 'bg-sidebar-foreground/20'
          )}
        />
        <WorkflowStep step={3} currentStep={currentStep} label='Track' isOpen={isOpen} />
      </div>
    </div>
  );
};

/* eslint-disable max-lines-per-function, complexity, max-len */
const StandaloneSidebar: React.FC<StandaloneSidebarProps> = ({
  user,
  isMobileMenuOpen: externalMobileMenuOpen,
  onMobileMenuToggle
}) => {
  const [open, setOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const queryClient = useQueryClient();
  const tenantId = useCurrentTenantId();

  // Use external control if provided, otherwise use internal state
  const mobileMenuOpen =
    externalMobileMenuOpen !== undefined ? externalMobileMenuOpen : isMobileMenuOpen;
  const setMobileMenuOpen = onMobileMenuToggle || setIsMobileMenuOpen;
  const location = useLocation();
  const navigate = useNavigate();
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const { hasPermission, signOut, user: authUser } = useAuth();
  const userId = authUser?.id;
  const {
    showBadge,
    isPopoverOpen,
    setIsPopoverOpen,
    releaseId,
    features,
    releaseHistory,
    onDismiss
  } = useWhatsNew();
  const [profileData, setProfileData] = useState<{ profile_completion_percentage?: number } | null>(
    null
  );
  const [loadingProfile, setLoadingProfile] = useState(false);
  const { data: userTenants } = useTenants();
  const { currentTenant } = useTenant();
  const [configOpen, setConfigOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);

  // Auto-expand collapsible sections when navigating to their child routes
  useEffect(() => {
    const configPaths = ['/campaigns', '/technicians', '/dealers'];
    const adminPaths = ['/admin/control-center', '/admin/analytics', '/admin/feedback'];

    if (configPaths.includes(location.pathname)) {
      setConfigOpen(true);
    }
    if (adminPaths.includes(location.pathname)) {
      setAdminOpen(true);
    }
  }, [location.pathname]);

  // Check if we're on mobile/tablet
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, setMobileMenuOpen]);

  // Load profile data
  useEffect(() => {
    if (authUser?.id) {
      loadProfileData();
    }
  }, [authUser?.id]);

  const loadProfileData = async () => {
    if (!authUser?.id) return;
    try {
      setLoadingProfile(true);
      const profile = await profileAPI.getUserProfile(authUser.id);
      setProfileData(profile);
    } catch (error) {
      // Silently fail - profile data is optional
    } finally {
      setLoadingProfile(false);
    }
  };

  // Determine current workflow step based on current page
  const getCurrentWorkflowStep = () => {
    if (location.pathname === '/recalls' || location.pathname === '/recall-map') {
      return 1; // Search
    } else if (
      location.pathname === '/routing/manual' ||
      location.pathname === '/routing/preview'
    ) {
      return 2; // Plan
    } else if (location.pathname === '/route-tracker' || location.pathname === '/performance') {
      return 3; // Track
    } else if (location.pathname === '/') {
      return 0; // Home shows workflow steps but none highlighted
    }
    return undefined; // Default to no workflow steps for other pages
  };

  const currentStep = getCurrentWorkflowStep();

  // Build navigation groups with permission requirements
  const allNavigationGroups: NavGroup[] = [
    {
      title: 'Home',
      items: [{ title: 'Home', url: '/', icon: Home }]
    },
    {
      title: '1. Search',
      items: [
        {
          title: 'Recall List',
          url: '/recalls',
          icon: Search,
          requiredPermission: { resource: 'recalls', action: 'read' }
        },
        {
          title: 'Recall Map',
          url: '/recall-map',
          icon: Map,
          requiredPermission: { resource: 'recalls', action: 'read' }
        }
      ]
    },
    {
      title: '2. Plan',
      items: [
        {
          title: 'Technician Assignment',
          url: '/routing/manual',
          icon: ClipboardCheck,
          requiredPermission: { resource: 'recalls', action: 'read' }
        },
        {
          title: 'Route Preview',
          url: '/routing/preview',
          icon: Eye,
          requiredPermission: { resource: 'recalls', action: 'read' }
        }
      ]
    },
    {
      title: '3. Track',
      items: [
        {
          title: 'Route Tracker',
          url: '/route-tracker',
          icon: RouteIcon,
          requiredPermission: { resource: 'recalls', action: 'read' }
        }
        // Performance page hidden for now
        // {
        //   title: 'Performance',
        //   url: '/performance',
        //   icon: BarChart3,
        //   requiredPermission: { resource: 'recalls', action: 'read' }
        // }
      ]
    },
    {
      title: 'Configuration',
      items: [
        {
          title: 'Campaigns',
          url: '/campaigns',
          icon: AlertCircle,
          requiredPermission: { resource: 'campaigns', action: 'read' }
        },
        {
          title: 'Technicians',
          url: '/technicians',
          icon: Users,
          requiredPermission: { resource: 'technicians', action: 'read' }
        },
        {
          title: 'Dealers',
          url: '/dealers',
          icon: Building2,
          requiredPermission: { resource: 'dealers', action: 'read' }
        }
      ]
    },
    {
      title: 'Admin',
      items: [
        {
          title: 'Control Center',
          url: '/admin/control-center',
          icon: Shield,
          requiredPermission: { resource: 'users', action: 'manage' }
        },
        {
          title: 'Analytics',
          url: '/admin/analytics',
          icon: BarChart3,
          requiredPermission: { resource: 'users', action: 'manage' }
        },
        {
          title: 'Feedback',
          url: '/admin/feedback',
          icon: MessageCircle,
          requiredPermission: { resource: 'users', action: 'manage' }
        }
      ]
    }
  ];

  // Filter navigation items based on user permissions
  const navigationGroups = useMemo(() => {
    return allNavigationGroups
      .map(group => ({
        ...group,
        items: group.items.filter(item => {
          // If no permission required, show the item
          if (!item.requiredPermission) {
            return true;
          }

          // Check if user has the required permission
          return hasPermission({
            resource: item.requiredPermission.resource,
            action: item.requiredPermission.action
          });
        })
      }))
      .filter(group => group.items.length > 0); // Remove empty groups
  }, [hasPermission, allNavigationGroups]);

  // Get user initials for avatar
  const getUserInitials = () => {
    const currentUser = user || authUser;
    if (user && 'name' in user && user.name) {
      return user.name
        .split(' ')
        .map((n: string) => n[0])
        .join('')
        .toUpperCase();
    }
    if (currentUser?.email) {
      return currentUser.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // Get user display name
  const getUserDisplayName = () => {
    const currentUser = user || authUser;
    return (user && 'name' in user && user.name) || currentUser?.email?.split('@')[0] || 'User';
  };

  // Get default tenant name - prefer currentTenant from JWT/context, fallback to first from list
  const getDefaultTenantName = () => {
    // First, try to use the current tenant from TenantContext (which uses JWT)
    if (currentTenant) {
      return currentTenant.tenant_name || currentTenant.name || null;
    }

    // Fallback to first tenant from userTenants list
    if (!userTenants || userTenants.length === 0) {
      return null;
    }
    return userTenants[0]?.tenant_name || userTenants[0]?.name || null;
  };

  // Handle sign out
  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success('Signed out successfully');
      navigate('/auth');
    } catch (error) {
      toast.error('Failed to sign out');
    }
  };

  // Keyboard shortcut for sign out
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'q') {
        e.preventDefault();
        handleSignOut();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  return (
    <>
      {/* Mobile backdrop overlay */}
      {mobileMenuOpen && isMobile && (
        <div
          className='fixed inset-0 bg-black/50 z-40 lg:hidden'
          onClick={() => setMobileMenuOpen(false)}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              setMobileMenuOpen(false);
            }
          }}
          role='button'
          tabIndex={0}
          aria-label='Close menu'
        />
      )}

      <div
        className={cn(
          'flex flex-col bg-sidebar text-sidebar-foreground border-r border-sidebar-border transition-all duration-300',
          // Desktop: takes full height of parent flex container
          'lg:h-full',
          // Mobile: fixed overlay with transform, full height
          'fixed inset-y-0 left-0 z-50 lg:relative lg:translate-x-0 h-screen',
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          // Width responsive
          open ? 'w-64 lg:w-64 md:w-56' : 'w-16 lg:w-16'
        )}
      >
        {/* Header */}
        <div
          className={cn('border-b border-sidebar-border backdrop-blur-sm', open ? 'p-4' : 'p-2')}
        >
          {/* Mobile close button */}
          {isMobile && (
            <div className='flex justify-end mb-2 lg:hidden'>
              <Button
                variant='ghost'
                size='sm'
                onClick={() => setMobileMenuOpen(false)}
                className='h-8 w-8 p-0 hover:bg-sidebar-accent text-sidebar-foreground/70 hover:text-sidebar-foreground'
              >
                <X className='h-4 w-4' />
              </Button>
            </div>
          )}

          <div className={cn('flex flex-col items-center', open ? 'gap-3' : 'gap-2')}>
            {/* Logo - Centered and Clickable */}
            <button
              onClick={() => {
                navigate('/');
                if (isMobile) setMobileMenuOpen(false);
              }}
              className={cn(
                'flex flex-col items-center hover:opacity-90 transition-all duration-200 hover:scale-105',
                open ? 'gap-3' : 'gap-1'
              )}
            >
              <div
                className={cn(
                  'rounded-full bg-primary-foreground shadow-lg flex items-center justify-center overflow-hidden transition-all duration-200 ring-2 ring-primary/20',
                  open ? 'w-16 h-16' : 'w-10 h-10'
                )}
              >
                <img
                  src={logo}
                  alt='Vehix'
                  className={cn(
                    'object-cover transition-all duration-200',
                    open ? 'h-12 w-12' : 'h-8 w-8'
                  )}
                />
              </div>
              {open && (
                <div className='text-center'>
                  <VehixName className='font-bold text-lg text-white' />
                  <div className='text-xs text-sidebar-foreground/60'>
                    AI-Powered Recall Intelligence
                  </div>
                  {getDefaultTenantName() && (
                    <div className='mt-2 text-xs font-medium text-sidebar-foreground/80 truncate max-w-[200px]'>
                      {getDefaultTenantName()}
                    </div>
                  )}
                </div>
              )}
            </button>

            {/* Toggle Button - Desktop only */}
            <Button
              variant='ghost'
              size='icon'
              onClick={() => setOpen(!open)}
              className={cn(
                'h-8 w-8 hover:bg-sidebar-accent text-sidebar-foreground/70 hover:text-sidebar-foreground',
                'hidden lg:flex lg:items-center lg:justify-center' // Hide on mobile, show on desktop with flex centering
              )}
            >
              {open ? (
                <PanelLeftClose className='h-4 w-4' />
              ) : (
                <PanelLeftOpen className='h-4 w-4' />
              )}
            </Button>
          </div>

          {/* Workflow Progress Indicator - Only show when sidebar is expanded */}
          {open && currentStep !== undefined && (
            <WorkflowProgress currentStep={currentStep} isOpen={open} />
          )}
        </div>

        {/* Navigation Content */}
        <div className='flex-1 overflow-y-auto scrollbar-auto-hide p-2'>
          {navigationGroups.map(group => {
            const isConfigGroup = group.title === 'Configuration';
            const isAdminGroup = group.title === 'Admin';
            const isCollapsible = isConfigGroup || isAdminGroup;
            const isOpen = isConfigGroup ? configOpen : isAdminGroup ? adminOpen : true;

            if (isCollapsible && open) {
              return (
                <Collapsible
                  key={group.title}
                  open={isOpen}
                  onOpenChange={isConfigGroup ? setConfigOpen : setAdminOpen}
                  className='mb-4'
                >
                  <CollapsibleTrigger asChild>
                    <button
                      className={cn(
                        'w-full flex items-center justify-between px-3 py-2 mb-2 rounded-lg text-xs font-semibold text-sidebar-foreground/50 uppercase tracking-wider hover:bg-sidebar-accent/50 transition-colors'
                      )}
                    >
                      <span>{group.title}</span>
                      {isOpen ? (
                        <ChevronDown className='h-3 w-3' />
                      ) : (
                        <ChevronRight className='h-3 w-3' />
                      )}
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className='space-y-0.5'>
                      {group.items.map(item => {
                        const isActive = location.pathname === item.url;
                        const handleMouseEnter = () => {
                          // Prefetch data when hovering over navigation links
                          if (item.url === '/dealers' && userId && tenantId) {
                            queryClient.prefetchQuery({
                              queryKey: ['dealers', userId, tenantId],
                              queryFn: () => fetchDealersData(userId, tenantId),
                              staleTime: 15 * 60 * 1000
                            });
                          } else if (item.url === '/campaigns' && userId && tenantId) {
                            queryClient.prefetchQuery({
                              queryKey: ['campaigns', userId, tenantId],
                              queryFn: () => fetchCampaignsData(userId, tenantId),
                              staleTime: 10 * 60 * 1000
                            });
                          } else if (item.url === '/technicians' && tenantId) {
                            queryClient.prefetchQuery({
                              queryKey: ['technicians', tenantId],
                              queryFn: () => fetchTechniciansData(),
                              staleTime: 10 * 60 * 1000
                            });
                          }
                        };
                        return (
                          <NavLink
                            key={item.url}
                            to={item.url}
                            onMouseEnter={handleMouseEnter}
                            onClick={() => {
                              if (isMobile) setMobileMenuOpen(false);
                            }}
                            className={cn(
                              'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 relative group',
                              'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                              isActive
                                ? 'bg-sidebar-accent text-sidebar-accent-foreground font-semibold shadow-sm border-l-4 border-l-primary pl-2'
                                : 'hover:pl-3 border-l-4 border-l-transparent'
                            )}
                          >
                            <item.icon
                              className={cn(
                                'h-4 w-4 flex-shrink-0 transition-colors',
                                isActive
                                  ? 'text-primary'
                                  : 'text-sidebar-foreground/70 group-hover:text-primary'
                              )}
                            />
                            <span className='truncate'>{item.title}</span>
                            {isActive && (
                              <div className='absolute inset-0 bg-primary/5 rounded-lg -z-10' />
                            )}
                          </NavLink>
                        );
                      })}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            }

            // Non-collapsible groups
            return (
              <div key={group.title} className='mb-4'>
                {open && (
                  <div className='text-xs font-semibold text-sidebar-foreground/50 uppercase tracking-wider mb-2 px-3'>
                    {group.title}
                  </div>
                )}
                <div className='space-y-0.5'>
                  {group.items.map(item => {
                    const isActive = location.pathname === item.url;
                    const handleMouseEnter = () => {
                      // Prefetch dealers data when hovering over dealers link
                      if (item.url === '/dealers' && userId && tenantId) {
                        queryClient.prefetchQuery({
                          queryKey: ['dealers', userId, tenantId],
                          queryFn: () => fetchDealersData(userId, tenantId),
                          staleTime: 15 * 60 * 1000
                        });
                      }
                    };
                    return (
                      <NavLink
                        key={item.url}
                        to={item.url}
                        onMouseEnter={handleMouseEnter}
                        onClick={() => {
                          if (isMobile) setMobileMenuOpen(false);
                        }}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 relative group',
                          'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                          isActive
                            ? 'bg-sidebar-accent text-sidebar-accent-foreground font-semibold shadow-sm border-l-4 border-l-primary pl-2'
                            : 'hover:pl-3 border-l-4 border-l-transparent',
                          !open && 'justify-center px-2'
                        )}
                      >
                        <item.icon
                          className={cn(
                            'h-4 w-4 flex-shrink-0 transition-colors',
                            isActive
                              ? 'text-primary'
                              : 'text-sidebar-foreground/70 group-hover:text-primary'
                          )}
                        />
                        {open && <span className='truncate'>{item.title}</span>}
                        {isActive && open && (
                          <div className='absolute inset-0 bg-primary/5 rounded-lg -z-10' />
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className='border-t border-sidebar-border p-3 bg-sidebar-accent/30'>
          <div className='flex flex-col gap-2'>
            {/* What's New Button */}
            <WhatsNewPopover
              trigger={
                <Button
                  variant='ghost'
                  size='sm'
                  className={cn(
                    'justify-start h-9 px-3 relative text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground w-full',
                    !open && 'px-2 justify-center'
                  )}
                >
                  <Sparkles className='h-4 w-4' />
                  {open && <span className='ml-2 text-xs'>What&apos;s New</span>}
                  {showBadge && (
                    <Badge
                      variant='default'
                      className='absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center shadow-lg'
                    >
                      <span className='sr-only'>New updates available</span>
                    </Badge>
                  )}
                </Button>
              }
              open={isPopoverOpen}
              onOpenChange={setIsPopoverOpen}
              releaseId={releaseId}
              features={features}
              releaseHistory={releaseHistory}
              onDismiss={onDismiss}
            />

            {/* User Profile Button - Opens Profile Dialog */}
            <button
              onClick={() => setProfileDialogOpen(true)}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-sidebar-accent w-full text-left transition-all duration-200 group cursor-pointer',
                !open && 'justify-center px-2'
              )}
            >
              <div className='relative'>
                <Avatar className='h-8 w-8 flex-shrink-0 ring-2 ring-primary/30 group-hover:ring-primary/60 transition-all'>
                  <AvatarImage
                    src={
                      profileData?.profile_photo_url ||
                      (user && 'avatar' in user ? user.avatar : undefined) ||
                      undefined
                    }
                    alt={getUserDisplayName()}
                  />
                  <AvatarFallback className='text-xs bg-primary text-white font-semibold'>
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
                {/* Profile completion indicator */}
                {/* eslint-disable indent */}
                {!loadingProfile &&
                  profileData &&
                  profileData.profile_completion_percentage !== undefined &&
                  profileData.profile_completion_percentage < 100 && (
                    <div className='absolute -bottom-1 -right-1 w-3 h-3 bg-orange-500 rounded-full border-2 border-sidebar' />
                  )}
                {/* eslint-enable indent */}
              </div>
              {open && (
                <div className='flex-1 min-w-0'>
                  <div className='text-sm font-semibold text-sidebar-foreground truncate'>
                    {getUserDisplayName()}
                  </div>
                  <div className='text-xs text-sidebar-foreground/50 truncate'>
                    {(user || authUser)?.email || 'user@example.com'}
                  </div>
                </div>
              )}
              {user && 'approved' in user && user.approved === false && (
                <div className='w-2 h-2 bg-yellow-400 rounded-full shadow-lg' />
              )}
            </button>
          </div>
        </div>

        {/* Profile Dialog */}
        <ProfileDialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen} />
      </div>
    </>
  );
};

// Export with mobile menu control
export { StandaloneSidebar };
/* eslint-enable max-lines-per-function, complexity, max-len */
